import { Stack, styled, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DeleteIcon,
	InputImage,
	RoundedReplaceIcon,
} from '../../../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../../../assets/theme/Colors';
import { getS3PresignedUrl, uploadFileToS3 } from '../../../services/S3Service';
import { FacebookMedia } from '../../../types';
import FacebookActionIconButton from './FacebookActionIconButton';

interface FacebookMediaListProps {
	media: Array<FacebookMedia>;
	postId?: string;
	editable?: boolean;
	disabled?: boolean;
	scrollable?: boolean;
	onChange?: (media: Array<FacebookMedia>) => void;
	onUploadingChange?: (isUploading: boolean) => void;
}

function FacebookMediaList({
	media,
	postId,
	editable = false,
	disabled = false,
	scrollable = false,
	onChange,
	onUploadingChange,
}: FacebookMediaListProps) {
	if (editable) {
		return (
			<EditableMediaList
				media={media}
				postId={postId}
				disabled={disabled}
				onChange={onChange}
				onUploadingChange={onUploadingChange}
			/>
		);
	}

	return <MediaPreview media={media} scrollable={scrollable} />;
}

function EditableMediaList({
	media,
	postId,
	disabled,
	onChange,
	onUploadingChange,
}: {
	media: Array<FacebookMedia>;
	postId?: string;
	disabled: boolean;
	onChange?: (media: Array<FacebookMedia>) => void;
	onUploadingChange?: (isUploading: boolean) => void;
}) {
	const [isUploading, setIsUploading] = useState(false);
	const [uploadError, setUploadError] = useState('');

	useEffect(() => {
		onUploadingChange?.(isUploading);
	}, [isUploading, onUploadingChange]);

	function getMediaType(file: File): FacebookMedia['type'] {
		const isDocx =
			file.type ===
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
			file.name.toLowerCase().endsWith('.docx');

		return file.type === 'application/pdf'
			? 'pdf'
			: isDocx
				? 'docx'
				: 'image';
	}

	async function makeMediaItem(file: File): Promise<FacebookMedia> {
		const s3Url = await uploadFileToS3(postId || 'facebook-posts', file);

		return {
			type: getMediaType(file),
			s3Url,
			fileName: file.name,
		};
	}

	async function onUploadFiles(event: React.ChangeEvent<HTMLInputElement>) {
		const files = Array.from(event.target.files || []);
		event.target.value = '';

		if (files.length > 0 && onChange && !disabled) {
			setIsUploading(true);
			setUploadError('');
			try {
				const uploadedMedia = await Promise.all(
					files.map(makeMediaItem),
				);
				onChange([...media, ...uploadedMedia]);
			} catch (error) {
				setUploadError(getUploadErrorMessage(error));
			} finally {
				setIsUploading(false);
			}
		}
	}

	async function onReplaceFile(
		event: React.ChangeEvent<HTMLInputElement>,
		index: number,
	) {
		const file = event.target.files?.[0];
		event.target.value = '';

		if (file && onChange && !disabled) {
			setIsUploading(true);
			setUploadError('');
			try {
				const uploadedMedia = await makeMediaItem(file);
				onChange(
					media.map((item, i) =>
						i === index ? uploadedMedia : item,
					),
				);
			} catch (error) {
				setUploadError(getUploadErrorMessage(error));
			} finally {
				setIsUploading(false);
			}
		}
	}

	function onDeleteMedia(index: number) {
		if (onChange && !disabled) {
			onChange(media.filter((_, i) => i !== index));
		}
	}

	return (
		<Stack spacing={1}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between">
				<Typography fontSize="12px" fontWeight={700} color="#6B7280">
					Images / PDFs / DOCX
				</Typography>
				<Tooltip title="Upload image, PDF, or DOCX">
					<UploadIconLabel disabled={disabled ? 1 : 0}>
						<InputImage />
						<input
							hidden
							type="file"
							disabled={disabled}
							accept="image/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
							multiple
							onChange={onUploadFiles}
						/>
					</UploadIconLabel>
				</Tooltip>
			</Stack>
			{isUploading && (
				<Typography fontSize="12px" color="#6B7280">
					Uploading to S3...
				</Typography>
			)}
			{uploadError && (
				<Typography fontSize="12px" color="error">
					{uploadError}
				</Typography>
			)}
			{media.length > 0 ? (
				<Stack spacing={1}>
					{media.map((item, index) => (
						<MediaEditorRow key={`${item.s3Url}-${index}`}>
							<MediaItem media={item} large={false} />
							<Stack sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									fontSize="12px"
									fontWeight={500}
									color="#111827"
									sx={{ wordBreak: 'break-all' }}>
									{item.fileName ||
										(item.type === 'image'
											? 'Image'
											: item.type === 'pdf'
												? 'PDF'
												: 'DOCX')}
								</Typography>
								<Typography fontSize="10px" color="#6B7280">
									Uploaded to S3
								</Typography>
							</Stack>
							<Tooltip title="Replace">
								<UploadIconLabel disabled={disabled ? 1 : 0}>
									<RoundedReplaceIcon />
									<input
										hidden
										type="file"
										disabled={disabled}
										accept="image/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
										onChange={event =>
											onReplaceFile(event, index)
										}
									/>
								</UploadIconLabel>
							</Tooltip>
							<Tooltip title="Delete">
								<FacebookActionIconButton
									disabled={disabled}
									onClick={() => onDeleteMedia(index)}>
									<DeleteIcon />
								</FacebookActionIconButton>
							</Tooltip>
						</MediaEditorRow>
					))}
				</Stack>
			) : (
				<Typography fontSize="12px" color="rgba(0, 0, 0, 0.55)">
					No media uploaded
				</Typography>
			)}
		</Stack>
	);
}

function MediaPreview({
	media,
	scrollable,
}: {
	media: Array<FacebookMedia>;
	scrollable: boolean;
}) {
	if (media.length === 0) {
		return (
			<TableRowText>{scrollable ? 'N/A' : 'No attachments'}</TableRowText>
		);
	}

	if (scrollable) {
		return (
			<TableMediaPreview>
				<TableMediaScrollRow>
					{media.map((item, index) => (
						<MediaItem
							key={`${item.s3Url}-${index}`}
							media={item}
							large={false}
						/>
					))}
				</TableMediaScrollRow>
			</TableMediaPreview>
		);
	}

	return (
		<Stack spacing={1}>
			<Typography fontSize="12px" fontWeight={700} color="#6B7280">
				Attachments (Images / PDFs / DOCX)
			</Typography>
			<Stack
				direction="row"
				spacing={2}
				useFlexGap
				flexWrap="wrap"
				sx={{ paddingLeft: '32px' }}>
				{media.map((item, index) => (
					<Stack
						key={`${item.s3Url}-${index}`}
						alignItems="center"
						spacing={0.5}
						sx={{ maxWidth: '110px' }}>
						<MediaItem media={item} large />
						<Typography
							fontSize="10px"
							color="#6B7280"
							noWrap
							title={item.fileName}
							sx={{ maxWidth: '100%' }}>
							{item.fileName ||
								(item.type === 'image'
									? 'Image'
									: item.type === 'pdf'
										? 'PDF'
										: 'DOCX')}
						</Typography>
					</Stack>
				))}
			</Stack>
		</Stack>
	);
}

function MediaItem({ media, large }: { media: FacebookMedia; large: boolean }) {
	const [presignedURL, setPresignedURL] = useState('');

	useEffect(() => {
		getS3PresignedUrl(media.s3Url).then(setPresignedURL);
	}, [media.s3Url]);

	if (media.type === 'image') {
		return (
			<MediaAnchor href={presignedURL} target="_blank" rel="noreferrer">
				<MediaImage
					src={presignedURL}
					alt="Facebook post media"
					large={large ? 1 : 0}
				/>
			</MediaAnchor>
		);
	}

	return (
		<MediaAnchor href={presignedURL} target="_blank" rel="noreferrer">
			<PdfBox large={large ? 1 : 0}>
				<DocumentCorner />
				<DocumentText>
					{media.type === 'pdf' ? 'PDF' : 'DOCX'}
				</DocumentText>
			</PdfBox>
		</MediaAnchor>
	);
}

function getUploadErrorMessage(error: unknown) {
	if (error instanceof Error) {
		return `Unable to upload to S3: ${error.message}`;
	}

	return 'Unable to upload to S3';
}

export const TableRowText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '400',
	color: 'rgba(0, 0, 0, 1)',
}));

const UploadIconLabel = styled('label')<{ disabled?: number }>(
	({ disabled }) => ({
		alignItems: 'center',
		color: MAIN_THEME_COLOR,
		cursor: disabled ? 'default' : 'pointer',
		display: 'inline-flex',
		justifyContent: 'center',
		height: '32px',
		opacity: disabled ? 0.45 : 1,
		pointerEvents: disabled ? 'none' : 'auto',
		width: '32px',
		'&:hover': {
			backgroundColor: disabled ? 'transparent' : '#EBFAF8',
			borderRadius: '50%',
		},
	}),
);

const MediaEditorRow = styled(Stack)(() => ({
	alignItems: 'center',
	border: '1px solid #E5E7EB',
	borderRadius: '6px',
	flexDirection: 'row',
	gap: '8px',
	padding: '8px',
}));

const TableMediaPreview = styled('div')(() => ({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
}));

const TableMediaScrollRow = styled('div')(() => ({
	display: 'flex',
	gap: '8px',
	maxWidth: '150px',
	overflowX: 'auto',
	overflowY: 'hidden',
	paddingBottom: '2px',
	'&::-webkit-scrollbar': {
		height: '3px',
	},
	'&::-webkit-scrollbar-track': {
		background: 'transparent',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#D1D5DB',
		borderRadius: '2px',
	},
}));

const MediaImage = styled('img')<{ large: number }>(({ large }) => ({
	width: large ? '180px' : '54px',
	height: large ? '120px' : '44px',
	objectFit: 'cover',
	borderRadius: '6px',
}));

const PdfBox = styled('div')<{ large: number }>(({ large }) => ({
	width: large ? '180px' : '54px',
	height: large ? '120px' : '44px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'relative',
	borderRadius: '6px',
	backgroundColor: '#F8FAFC',
	border: '1px solid #CBD5E1',
	padding: '4px',
	flexShrink: 0,
}));

const DocumentCorner = styled('span')(() => ({
	borderLeft: '8px solid transparent',
	borderTop: '8px solid #CBD5E1',
	position: 'absolute',
	right: '4px',
	top: '4px',
}));

const DocumentText = styled(Typography)(() => ({
	color: MAIN_THEME_COLOR,
	fontSize: '9px',
	fontWeight: 700,
	lineHeight: 1,
	textAlign: 'center',
}));

const MediaAnchor = styled('a')(() => ({
	display: 'inline-flex',
	flexShrink: 0,
	'&:hover': { opacity: 0.85 },
}));

export default FacebookMediaList;
